import { db } from '../config/firebase';
import { collection, doc, setDoc, getDocs, deleteDoc, query, where, orderBy, Timestamp } from 'firebase/firestore';

interface StoredRecording {
  id: string;
  sessionId: string;
  patientId?: string;
  metadata: {
    startTime: Date;
    endTime: Date;
    duration: number;
    sampleRate: number;
    channels: number;
    originalFileSize: number;
    filteredFileSize: number;
    silenceRemoved: number;
    noiseReduction: boolean;
    securityLevel: 'standard' | 'hipaa' | 'encrypted';
  };
  transcript?: string;
  notes?: string;
  originalAudioUrl?: string;
  filteredAudioUrl?: string;
  encryptionKey?: string;
  accessLog: AccessLogEntry[];
  tags: string[];
}

interface AccessLogEntry {
  timestamp: Date;
  userId: string;
  action: 'created' | 'accessed' | 'downloaded' | 'deleted' | 'shared';
  ipAddress?: string;
  userAgent?: string;
}

const COLLECTION_NAME = 'recording_metadata';

class RecordingStorageService {
  /**
   * Store a new recording with security measures
   */
  async storeRecording(
    originalBlob: Blob,
    filteredBlob: Blob,
    metadata: any,
    sessionId: string,
    patientId?: string,
    notes?: string
  ): Promise<string> {
    const recordingId = this.generateSecureId();

    try {
      // Convert blobs to secure storage format
      const originalAudioUrl = await this.secureStoreBlob(originalBlob, `${recordingId}_original`);
      const filteredAudioUrl = await this.secureStoreBlob(filteredBlob, `${recordingId}_filtered`);

      // Create recording record
      const recording: StoredRecording = {
        id: recordingId,
        sessionId,
        patientId,
        metadata: {
          ...metadata,
          securityLevel: patientId ? 'hipaa' : 'standard'
        },
        notes,
        originalAudioUrl,
        filteredAudioUrl,
        accessLog: [{
          timestamp: new Date(),
          userId: 'current_user', // Would come from auth context
          action: 'created'
        }],
        tags: this.generateTags(sessionId, patientId, metadata)
      };

      // Encrypt sensitive data if patient-related
      if (patientId) {
        recording.encryptionKey = await this.generateEncryptionKey();
        recording.metadata.securityLevel = 'encrypted';
      }

      // Store in Firestore
      await this.secureStoreRecord(recording);

      // Log successful storage
      console.log(`Recording ${recordingId} stored securely`);

      return recordingId;

    } catch (error) {
      console.error('Failed to store recording:', error);
      throw new Error('Recording storage failed - please try again');
    }
  }

  /**
   * Retrieve a recording with access logging
   */
  async getRecording(recordingId: string, userId: string): Promise<StoredRecording | null> {
    try {
      const recording = await this.getStoredRecord(recordingId);

      if (!recording) {
        return null;
      }

      // Log access
      recording.accessLog.push({
        timestamp: new Date(),
        userId,
        action: 'accessed'
      });

      await this.secureStoreRecord(recording);

      return recording;

    } catch (error) {
      console.error('Failed to retrieve recording:', error);
      return null;
    }
  }

  /**
   * Get recordings for a specific session
   */
  async getSessionRecordings(sessionId: string): Promise<StoredRecording[]> {
    try {
      const q = query(
        collection(db, COLLECTION_NAME),
        where('sessionId', '==', sessionId)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(docSnap => this.deserializeRecording(docSnap.data()));
    } catch (error) {
      console.error('Failed to get session recordings:', error);
      return [];
    }
  }

  /**
   * Get recordings for a specific patient
   */
  async getPatientRecordings(patientId: string): Promise<StoredRecording[]> {
    try {
      const q = query(
        collection(db, COLLECTION_NAME),
        where('patientId', '==', patientId)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(docSnap => this.deserializeRecording(docSnap.data()));
    } catch (error) {
      console.error('Failed to get patient recordings:', error);
      return [];
    }
  }

  /**
   * Delete a recording securely
   */
  async deleteRecording(recordingId: string, userId: string): Promise<boolean> {
    try {
      const recording = await this.getStoredRecord(recordingId);

      if (!recording) {
        return false;
      }

      // Log deletion attempt (fire-and-forget audit)
      recording.accessLog.push({
        timestamp: new Date(),
        userId,
        action: 'deleted'
      });

      // Secure deletion of audio blob docs
      if (recording.originalAudioUrl) {
        await this.secureDeleteBlob(recording.originalAudioUrl);
      }
      if (recording.filteredAudioUrl) {
        await this.secureDeleteBlob(recording.filteredAudioUrl);
      }

      // Remove encryption key doc
      if (recording.encryptionKey) {
        try {
          await deleteDoc(doc(db, COLLECTION_NAME, `key_${recordingId}`));
        } catch (_) { /* ignore */ }
      }

      // Remove record from Firestore
      await deleteDoc(doc(db, COLLECTION_NAME, recordingId));

      console.log(`Recording ${recordingId} deleted securely`);
      return true;

    } catch (error) {
      console.error('Failed to delete recording:', error);
      return false;
    }
  }

  /**
   * Export recording for external use
   */
  async exportRecording(recordingId: string, format: 'wav' | 'mp3' | 'json'): Promise<Blob | null> {
    try {
      const recording = await this.getStoredRecord(recordingId);

      if (!recording) {
        return null;
      }

      if (format === 'json') {
        // Export metadata and transcript
        const exportData = {
          id: recording.id,
          sessionId: recording.sessionId,
          patientId: recording.patientId,
          metadata: recording.metadata,
          transcript: recording.transcript,
          notes: recording.notes,
          tags: recording.tags,
          exportedAt: new Date().toISOString()
        };

        return new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      } else {
        // Export audio file
        const audioUrl = format === 'wav' ? recording.filteredAudioUrl : recording.originalAudioUrl;
        if (audioUrl) {
          return await this.getBlobFromUrl(audioUrl);
        }
      }

      return null;

    } catch (error) {
      console.error('Failed to export recording:', error);
      return null;
    }
  }

  /**
   * Get recording statistics
   */
  async getRecordingStats(): Promise<{
    totalRecordings: number;
    totalDuration: number;
    totalSize: number;
    byPatient: Record<string, number>;
    byDate: Record<string, number>;
  }> {
    const allRecordings = await this.getAllRecordings();

    const stats = {
      totalRecordings: allRecordings.length,
      totalDuration: allRecordings.reduce((sum, r) => sum + r.metadata.duration, 0),
      totalSize: allRecordings.reduce((sum, r) => sum + r.metadata.originalFileSize + r.metadata.filteredFileSize, 0),
      byPatient: {} as Record<string, number>,
      byDate: {} as Record<string, number>
    };

    allRecordings.forEach(recording => {
      // By patient
      if (recording.patientId) {
        stats.byPatient[recording.patientId] = (stats.byPatient[recording.patientId] || 0) + 1;
      }

      // By date
      const date = recording.metadata.startTime.toISOString().split('T')[0];
      stats.byDate[date] = (stats.byDate[date] || 0) + 1;
    });

    return stats;
  }

  // Private helper methods
  private generateSecureId(): string {
    return 'rec_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  private async generateEncryptionKey(): Promise<string> {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  private generateTags(sessionId: string, patientId?: string, metadata?: any): string[] {
    const tags = ['audio-recording'];

    if (patientId) {
      tags.push('patient-session');
    } else {
      tags.push('staff-meeting');
    }

    if (metadata?.silenceRemoved > 30) {
      tags.push('high-silence-removal');
    }

    if (metadata?.noiseReduction) {
      tags.push('noise-reduced');
    }

    const currentHour = new Date().getHours();
    if (currentHour >= 9 && currentHour <= 17) {
      tags.push('business-hours');
    } else {
      tags.push('after-hours');
    }

    return tags;
  }

  private async secureStoreBlob(blob: Blob, key: string): Promise<string> {
    // In production, this would upload to Firebase Storage
    // For now, store the data URL in a Firestore doc
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const dataUrl = reader.result as string;
          const blobDocId = `blob_${key}`;
          await setDoc(doc(db, COLLECTION_NAME, blobDocId), {
            dataUrl,
            createdAt: Timestamp.now()
          });
          resolve(blobDocId);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  private async secureStoreRecord(recording: StoredRecording): Promise<void> {
    const dataToStore = this.serializeRecording(recording);
    await setDoc(doc(db, COLLECTION_NAME, recording.id), dataToStore, { merge: true });
  }

  private async getStoredRecord(recordingId: string): Promise<StoredRecording | null> {
    try {
      const { getDoc: getDocSnap } = await import('firebase/firestore');
      const docRef = doc(db, COLLECTION_NAME, recordingId);
      const docSnap = await getDocSnap(docRef);

      if (!docSnap.exists()) return null;

      return this.deserializeRecording(docSnap.data());
    } catch (error) {
      console.error('Failed to get recording data:', error);
      return null;
    }
  }

  async getAllRecordings(): Promise<StoredRecording[]> {
    try {
      const q = query(collection(db, COLLECTION_NAME));
      const snapshot = await getDocs(q);
      const recordings: StoredRecording[] = [];

      snapshot.docs.forEach(docSnap => {
        const data = docSnap.data();
        // Skip blob documents
        if (docSnap.id.startsWith('blob_') || docSnap.id.startsWith('key_')) return;
        try {
          recordings.push(this.deserializeRecording(data));
        } catch (_) { /* skip malformed docs */ }
      });

      return recordings.sort((a, b) => b.metadata.startTime.getTime() - a.metadata.startTime.getTime());
    } catch (error) {
      console.error('Failed to get all recordings:', error);
      return [];
    }
  }

  private async secureDeleteBlob(blobDocId: string): Promise<void> {
    try {
      await deleteDoc(doc(db, COLLECTION_NAME, blobDocId));
    } catch (error) {
      console.error('Failed to delete blob doc:', error);
    }
  }

  private async getBlobFromUrl(blobDocId: string): Promise<Blob | null> {
    try {
      const { getDoc: getDocSnap } = await import('firebase/firestore');
      const docRef = doc(db, COLLECTION_NAME, blobDocId);
      const docSnap = await getDocSnap(docRef);

      if (!docSnap.exists()) return null;

      const dataUrl = docSnap.data().dataUrl;
      if (!dataUrl) return null;

      const response = await fetch(dataUrl);
      return await response.blob();
    } catch (error) {
      console.error('Failed to retrieve blob:', error);
      return null;
    }
  }

  /** Serialize recording for Firestore (Dates → Timestamps) */
  private serializeRecording(recording: StoredRecording): any {
    return {
      ...recording,
      metadata: {
        ...recording.metadata,
        startTime: recording.metadata.startTime instanceof Date
          ? Timestamp.fromDate(recording.metadata.startTime)
          : recording.metadata.startTime,
        endTime: recording.metadata.endTime instanceof Date
          ? Timestamp.fromDate(recording.metadata.endTime)
          : recording.metadata.endTime,
      },
      accessLog: recording.accessLog.map(entry => ({
        ...entry,
        timestamp: entry.timestamp instanceof Date
          ? Timestamp.fromDate(entry.timestamp)
          : entry.timestamp,
      })),
    };
  }

  /** Deserialize recording from Firestore (Timestamps → Dates) */
  private deserializeRecording(data: any): StoredRecording {
    return {
      ...data,
      metadata: {
        ...data.metadata,
        startTime: data.metadata.startTime instanceof Timestamp
          ? data.metadata.startTime.toDate()
          : new Date(data.metadata.startTime),
        endTime: data.metadata.endTime instanceof Timestamp
          ? data.metadata.endTime.toDate()
          : new Date(data.metadata.endTime),
      },
      accessLog: (data.accessLog || []).map((entry: any) => ({
        ...entry,
        timestamp: entry.timestamp instanceof Timestamp
          ? entry.timestamp.toDate()
          : new Date(entry.timestamp),
      })),
    } as StoredRecording;
  }
}

export const recordingStorage = new RecordingStorageService();
export type { StoredRecording, AccessLogEntry };
